import ClinicianPayoutPanel from "./ClinicianPayoutPanel.jsx";
import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import Button from "@/components/ui/Button.jsx";
import Card from "@/components/ui/Card.jsx";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import List from "@/sot/primitives/List.jsx";
import BasicDialog from "@/components/ui/BasicDialog.jsx";
import PreferencesPageShell from "@/features/settings/preferences/PreferencesPageShell.jsx";
import { getBillingHistory } from "@/services/billingLedgerService.js";
import { paymentMethodLabel } from "@/services/paymentPresentation.js";
import { downloadAppointmentFinancialPdf, financialDocumentDetails, printAppointmentFinancialPdf } from "@/services/appointmentFinancialDocumentService.js";
import { getMarketPaymentCoverage } from "@/services/marketPaymentService.js";
import { recoverSponsorPayments } from "@/lib/payments/recovery.js";

function formatCurrency(amount, currency = "CHF", locale = undefined) {
  const numeric = Number(amount);
  if (!Number.isFinite(numeric)) return "";
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
    }).format(numeric);
  } catch {
    return `${numeric} ${currency}`;
  }
}

function formatDate(value, locale = undefined) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatDateTime(value, locale = undefined) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatTransactionState(row = {}, t) {
  const status = String(row?.status || "").trim().toLowerCase();
  const appointmentState = String(row?.meta?.appointment_state_after_reversal || "")
    .trim()
    .toLowerCase();
  const paymentType = String(row?.type || "").trim().toLowerCase();

  if (status === "refund_pending") {
    return t(
      "settings.preferences.transactions.states.refundPending",
      "Cancelled / Refund in progress"
    );
  }

  if (status === "refunded") {
    return t("settings.preferences.transactions.states.refunded", "Cancelled / Refunded");
  }

  if (status === "cancelled" && appointmentState === "cancelled") {
    return t("settings.preferences.transactions.states.cancelled", "Cancelled");
  }

  if (status === "paid" || status === "succeeded" || status === "completed") {
    return paymentType === "pharmacy"
      ? t("settings.preferences.transactions.states.paidPharmacy", "Paid / Pharmacy")
      : t("settings.preferences.transactions.states.paid", "Paid");
  }

  if (status === "pending") {
    return t("settings.preferences.transactions.states.pending", "Pending");
  }

  return "";
}

function transactionStateClassName(row = {}) {
  const status = String(row?.status || "").trim().toLowerCase();
  if (["paid", "succeeded", "completed"].includes(status)) return "text-primary";
  if (["refund_pending", "refunded", "cancelled"].includes(status)) return "text-on-surface-var";
  return "text-on-surface-var";
}

function isPaidTransaction(row = {}) {
  const status = String(row?.status || "").trim().toLowerCase();
  return /paid|succeeded|completed/i.test(status);
}

function isRefundedTransaction(row = {}) {
  const status = String(row?.status || "").trim().toLowerCase();
  const appointmentState = String(row?.meta?.appointment_state_after_reversal || "")
    .trim()
    .toLowerCase();

  if (status === "refund_pending" || status === "refunded") return true;
  return status === "cancelled" && appointmentState === "cancelled";
}

function isOpenTransaction(row = {}) {
  const status = String(row?.status || "").trim().toLowerCase();
  return ["pending", "processing", "requires_action", "requires_payment", "requires_payment_method"].includes(
    status
  );
}

function MetricValue({ children }) {
  return <div className="mt-2 text-title-medium text-on-surface sm:text-title-large">{children}</div>;
}

function downloadCsv(filename, rows) {
  const csv = rows
    .map((row) =>
      row
        .map((value) => `"${String(value ?? "").replace(/"/g, '""')}"`)
        .join(",")
    )
    .join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export default function TransactionsInvoicesPage({ role = "patient" }) {
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, languageCode } = useLanguage();
  const documentOptions = React.useMemo(() => ({ t, languageCode }), [languageCode, t]);
  const auth = useAuth() || {};
  const userId = auth?.user?.id || null;
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [refreshToken, setRefreshToken] = React.useState(0);
  const [selectedRow, setSelectedRow] = React.useState(null);
  const [selectedDocumentKind, setSelectedDocumentKind] = React.useState("appointment");
  const [documentBusy, setDocumentBusy] = React.useState(false);

  const load = React.useCallback(async () => {
    let alive = true;
    setLoading(true);
    setError("");
    const sessionId = String(searchParams.get("session_id") || "").trim();
    const sponsorPayment = String(searchParams.get("sponsor_payment") || "").trim();
    let nextError = "";

    // Provider recovery is only relevant after the provider sends this page a
    // return marker. Do not make ordinary billing hydration wait on it.
    const recoveryResult = (sessionId || sponsorPayment)
      ? await recoverSponsorPayments({ role, userId, sessionId })
      : null;

    if (!alive) return;

    if (recoveryResult?.usedSessionId && sponsorPayment) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete("session_id");
      nextParams.delete("sponsor_payment");
      setSearchParams(nextParams, { replace: true });
    }

    if (recoveryResult?.lastError?.message) {
      nextError = t(
        "settings.preferences.transactions.errors.refreshSponsorPayments",
        "We could not refresh your sponsor payment records right now."
      );
    }

    const result = await getBillingHistory(role, userId);
    if (!alive) return;
    if (!nextError && result?.error?.message) {
      nextError = t(
        "settings.preferences.transactions.errors.load",
        "We could not load your billing history right now."
      );
    }
    setError(nextError);
    setRows(Array.isArray(result?.data) ? result.data : []);
    setLoading(false);
    return () => {
      alive = false;
    };
  }, [role, searchParams, setSearchParams, userId]);

  React.useEffect(() => {
    let released = false;
    let cleanup = () => {};

    void (async () => {
      const maybeCleanup = await load();
      if (!released && typeof maybeCleanup === "function") {
        cleanup = maybeCleanup;
      }
    })();

    return () => {
      released = true;
      cleanup();
    };
  }, [load, refreshToken]);

  React.useEffect(() => {
    function requestRefresh() {
      setRefreshToken((current) => current + 1);
    }

    function handleVisibilityChange() {
      if (document.visibilityState === "visible") {
        requestRefresh();
      }
    }

    window.addEventListener("focus", requestRefresh);
    window.addEventListener("afrimed:transactions:changed", requestRefresh);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("focus", requestRefresh);
      window.removeEventListener("afrimed:transactions:changed", requestRefresh);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const isDoctor = role === "doctor";
  const paidRows = React.useMemo(
    () =>
      rows.filter((row) =>
        isPaidTransaction(row) &&
        (!isDoctor || !String(row?.seller_role || "").trim() || row?.seller_role === "doctor")
      ),
    [isDoctor, rows]
  );
  const paidTotalsByCurrency = React.useMemo(() => {
    const totals = new Map();

    paidRows.forEach((row) => {
      const currency = String(row?.currency || "").trim().toUpperCase() || "CHF";
      const amount = Number(row?.amount || 0);
      if (!Number.isFinite(amount)) return;
      totals.set(currency, (totals.get(currency) || 0) + amount);
    });

    return Array.from(totals.entries()).map(([currency, amount]) => ({
      currency,
      amount,
    }));
  }, [paidRows]);
  const hasMixedPaidCurrencies = paidTotalsByCurrency.length > 1;
  const primaryPaidTotal = paidTotalsByCurrency[0] || null;
  const totalPaidLabel = hasMixedPaidCurrencies
    ? t("settings.preferences.transactions.totalPaidMixed", "Mixed currencies")
    : primaryPaidTotal
      ? formatCurrency(primaryPaidTotal.amount, primaryPaidTotal.currency)
      : "—";
  const refundedCount = React.useMemo(
    () => rows.filter((row) => isRefundedTransaction(row)).length,
    [rows]
  );
  const openCount = React.useMemo(
    () => rows.filter((row) => isOpenTransaction(row)).length,
    [rows]
  );
  const coverage = React.useMemo(() => getMarketPaymentCoverage(), []);

  return (
    <PreferencesPageShell
      role={role}
      title={t(
        "settings.preferences.transactions.title",
        "Transactions & invoices"
      )}
      subtitle={t(
        "settings.preferences.transactions.subtitleLong",
        isDoctor
          ? "Track consultation settlements, invoice visibility, and keep AfriMed™ exports close at hand."
          : "Track billing history, payment states, and keep receipts close at hand."
      )}
    >
      {role === "doctor" && <ClinicianPayoutPanel financialView />}
      <div className="grid gap-3 md:grid-cols-3">
        <Card className="p-4">
          <div className="text-label-large text-on-surface-var">
            {t("settings.preferences.transactions.totalPaid", "Total paid")}
          </div>
          <MetricValue>{totalPaidLabel}</MetricValue>
          {hasMixedPaidCurrencies ? (
            <div className="mt-2 text-body-small text-on-surface-var">
              {paidTotalsByCurrency
                .map(({ currency, amount }) => formatCurrency(amount, currency))
                .join(" • ")}
            </div>
          ) : null}
          {hasMixedPaidCurrencies ? (
            <div className="mt-2 text-body-small text-on-surface-var">
              {t(
                "settings.preferences.transactions.totalPaidMixedHelper",
                "Shown per currency. No FX conversion is applied to this total."
              )}
            </div>
          ) : null}
        </Card>
        <Card className="p-4">
          <div className="text-label-large text-on-surface-var">
            {t("settings.preferences.transactions.documentCount", "Documents")}
          </div>
          <MetricValue>{rows.length}</MetricValue>
        </Card>
        <Card className="p-4">
          <div className="text-label-large text-on-surface-var">
            {t(
              isDoctor
                ? "settings.preferences.transactions.refunded"
                : "settings.preferences.transactions.open",
              isDoctor ? "Refunded" : "Open"
            )}
          </div>
          <MetricValue>{isDoctor ? refundedCount : openCount}</MetricValue>
        </Card>
      </div>

      <div>
        <div className="max-w-[42rem]">
          <div className="text-title-small text-on-surface">
            {t(
              "settings.preferences.transactions.billingCenter",
              isDoctor ? "Operational shortcuts" : "Receipts and exports"
            )}
          </div>
          <div className="mt-1 text-body-medium text-on-surface-var">
            {t(
              "settings.preferences.transactions.billingCenterSubtitle",
          isDoctor
                ? "Manage AfriMed™ payment setup and settlement records in one place."
                : "Open an AfriMed™ receipt at any time, or export your billing history."
            )}
          </div>
        </div>

        <div className="mt-4 flex flex-col gap-2 sm:flex-row">
          <Button
            type="button"
            onClick={() =>
              nav(
                isDoctor
                  ? "/settings/doctor/preferences/plans"
                  : "/settings/patient/preferences/downloads"
              )
            }
            variant="default"
            size="default"
            className="w-full sm:w-auto"
          >
            {t(
              "settings.preferences.transactions.openCenter",
              isDoctor ? "Open billing setup" : "Open downloads"
            )}
          </Button>
          <Button
            type="button"
            onClick={() =>
              downloadCsv(
                `${role}-billing-history.csv`,
                [
                  ["Created", "Status", "Amount", "Currency", "Paid At"],
                  ...rows.map((row) => [
                    formatDateTime(row?.created_at),
                    row?.status || "",
                    row?.amount || "",
                    row?.currency || "",
                    formatDateTime(row?.paid_at),
                  ]),
                ]
              )
            }
            disabled={!rows.length}
            variant="tonal"
            size="default"
            className="w-full sm:w-auto"
          >
            {t(
              "settings.preferences.transactions.downloadCsv",
              "Download CSV"
            )}
          </Button>
        </div>
      </div>

      {!isDoctor ? (
        <div>
          <div className="max-w-[42rem]">
            <div className="text-title-small text-on-surface">
              {t(
                "settings.preferences.transactions.sponsorTitle",
                "Family and diaspora sponsor payments"
              )}
            </div>
            <div className="mt-2 text-body-medium text-on-surface-var">
              {t(
                "settings.preferences.transactions.sponsorBody",
                "Many patients are funded by family members across SADC and the diaspora. Prepare sponsor details once, then generate a secure AfriMed™ payment link that can be shared with family cleanly."
              )}
            </div>
          </div>

          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <Card className="bg-[rgb(var(--clr-surface))] p-4">
              <div className="text-label-large text-on-surface-var">
                {t("settings.preferences.transactions.sponsorSadc", "SADC markets")}
              </div>
              <MetricValue>{coverage.sadcCount}</MetricValue>
            </Card>
            <Card className="bg-[rgb(var(--clr-surface))] p-4">
              <div className="text-label-large text-on-surface-var">
                {t("settings.preferences.transactions.sponsorDiaspora", "Diaspora markets")}
              </div>
              <MetricValue>{coverage.diasporaCount}</MetricValue>
            </Card>
            <Card className="bg-[rgb(var(--clr-surface))] p-4">
              <div className="text-label-large text-on-surface-var">
                {t("settings.preferences.transactions.sponsorTotal", "Total coverage")}
              </div>
              <MetricValue>{coverage.totalCount}</MetricValue>
            </Card>
          </div>

          <div className="mt-4 flex flex-col gap-2 sm:flex-row">
            <Button
              type="button"
              variant="default"
              size="default"
              onClick={() => nav("/settings/patient/preferences/transactions/prepare-sponsor-payment")}
              className="w-full sm:w-auto"
            >
              {t(
                "settings.preferences.transactions.sponsorAction",
                "Prepare sponsor payment"
              )}
            </Button>
            <Button
              type="button"
              variant="tonal"
              size="default"
              onClick={() => nav("/settings/patient/preferences/plans")}
              className="w-full sm:w-auto"
            >
              {t(
                "settings.preferences.transactions.sponsorLearnMore",
                "How AfriMed™ handles payments"
              )}
            </Button>
          </div>
        </div>
      ) : null}

      <div>
        <div className="max-w-[42rem]">
          <div className="text-title-small text-on-surface">
            {t(
              "settings.preferences.transactions.recentTransactions",
              "Recent transactions"
            )}
          </div>
          <div className="mt-1 text-body-medium text-on-surface-var">
            {t(
              "settings.preferences.transactions.recentTransactionsSubtitle",
              isDoctor
                ? "Settlements, paid consultations, and AfriMed™ commission records will collect here as the billing lane fills out."
                : "Receipts, payment confirmations, and billing updates will collect here as new activity lands."
            )}
          </div>
        </div>
      </div>

      <Card className="overflow-hidden p-0">
        <List
          items={
            rows.length
              ? rows.slice(0, 8).map((row, index) => ({
                  leading: "/icons/list-items/receipt.svg",
                  title:
                    row?.appointment?.booked_service_name ||
                    (String(row?.type || "").trim().toLowerCase() === "pharmacy"
                      ? row?.meta?.external_rx_id || row?.prescription_id || ""
                      : "") ||
                    row?.invoice_number ||
                    row?.reference ||
                    t("settings.preferences.transactions.transaction", "Transaction"),
                  subtitle: [
                    formatDateTime(row?.paid_at || row?.created_at, languageCode),
                    row?.clinician_name,
                    paymentMethodLabel(row, t),
                  ].filter(Boolean).join(" • "),
                  trailing: (
                    <div className="text-right">
                      <div className="text-body-small text-on-surface-var">
                        {formatCurrency(row?.amount, row?.currency, languageCode) ||
                          (row?.status || "")}
                      </div>
                      {formatTransactionState(row, t) ? (
                        <div className={`mt-1 text-label-small ${transactionStateClassName(row)}`}>
                          {formatTransactionState(row, t)}
                        </div>
                      ) : null}
                    </div>
                  ),
                  divider: index !== Math.min(rows.length, 8) - 1,
                  onClick: () => {
                    setSelectedDocumentKind(row?.document_kind === "refund" ? "refund" : "appointment");
                    setSelectedRow(row);
                  },
                }))
              : [
                  {
                    leading: "/icons/list-items/receipt.svg",
                    title: t(
                      "settings.preferences.transactions.empty",
                      "No billing records yet"
                    ),
                    subtitle: t(
                      "settings.preferences.transactions.emptySubtitle",
                      isDoctor
                        ? "Transactions, settlements, and AfriMed™ commission records will appear here when billing starts."
                        : "Transactions and receipts will appear here when billing starts."
                    ),
                    divider: false,
                  },
                ]
          }
        />
      </Card>

      {loading ? (
        <p className="text-body-medium text-on-surface-var">
          {t("common.loading", "Loading...")}
        </p>
      ) : null}

      {error ? (
        <p className="text-body-medium text-[rgb(var(--clr-error))]">{error}</p>
      ) : null}

      <BasicDialog
        open={Boolean(selectedRow)}
        title={selectedRow
          ? financialDocumentDetails(selectedRow, selectedDocumentKind, documentOptions).title
          : t("payment.documents.appointmentReceipt", "Appointment receipt")}
        cancelText={t("common.close", "Close")}
        okText={documentBusy ? t("common.loading", "Loading...") : t("settings.preferences.transactions.downloadReceipt", "Download receipt")}
        disableOk={documentBusy}
        onCancel={() => setSelectedRow(null)}
        onOk={async () => {
          if (!selectedRow) return;
          setDocumentBusy(true);
          try { await downloadAppointmentFinancialPdf(selectedRow, selectedDocumentKind, documentOptions); } finally { setDocumentBusy(false); }
        }}
        body={selectedRow ? (() => {
          const document = financialDocumentDetails(selectedRow, selectedDocumentKind, documentOptions);
          const hasRefund = Boolean(selectedRow?.reversal);
          return <div className="space-y-3 text-body-medium text-on-surface">
            <div><span className="text-on-surface-var">{t("payment.documents.receiptReference", "Receipt reference")}</span><div>{document.reference}</div></div>
            <div><span className="text-on-surface-var">{t("payment.documents.appointment", "Appointment")}</span><div>{document.appointment}</div></div>
            <div><span className="text-on-surface-var">{t("payment.documents.service", "Service")}</span><div>{document.service}</div></div>
            <div><span className="text-on-surface-var">{t("payment.documents.clinician", "Clinician")}</span><div>{document.clinician}</div></div>
            <div><span className="text-on-surface-var">{t("payment.documents.paymentMethod", "Payment method")}</span><div>{document.paymentMethod}</div></div>
            <div><span className="text-on-surface-var">{selectedDocumentKind === "refund" ? t("payment.documents.refundStatus", "Refund status") : t("payment.documents.paymentStatus", "Payment status")}</span><div>{document.paymentState}</div></div>
            <div><span className="text-on-surface-var">{t("payment.documents.amount", "Amount")}</span><div>{document.amount}</div></div>
            {document.refundDestination ? <div><span className="text-on-surface-var">{t("payment.documents.refundDestination", "Refund destination")}</span><div>{document.refundDestination}</div></div> : null}
            <div className="flex flex-wrap gap-2 pt-1">
              {hasRefund ? <Button type="button" variant="tonal" size="sm" onClick={() => setSelectedDocumentKind(selectedDocumentKind === "refund" ? "appointment" : "refund")}>{selectedDocumentKind === "refund" ? t("payment.documents.viewAppointmentReceipt", "View appointment receipt") : t("payment.documents.viewRefundReceipt", "View refund receipt")}</Button> : null}
              <Button type="button" variant="tonal" size="sm" onClick={() => printAppointmentFinancialPdf(selectedRow, selectedDocumentKind, documentOptions)}>{t("payment.documents.print", "Print")}</Button>
            </div>
          </div>;
        })() : null}
      />

    </PreferencesPageShell>
  );
}
